import { FC, useCallback, useMemo } from "react";
import { ISiteConfigLayouts, MenuButtonsEnum } from "@finbackoffice/fe-core";
import { usePathnameWithLocal } from "hooks";
import { useSiteConfig } from "@finbackoffice/site-core";
import ActiveLink from "components/base/active-link/ActiveLink";
import { Svg } from "components/base/svg/Svg";
import { AssetsDirectory } from "@finbackoffice/enums";
import classNames from "classnames";
import { RouteNames } from "utils/constants";
import Translate from "components/base/translate/Translate";
import styles from "./sidebar-main-navigation.module.sass";

type MenuScopeType = "default" | "sport" | "casino";

const SidebarMainNavigation: FC = () => {
    const siteLayoutsConfig = useSiteConfig<ISiteConfigLayouts>("layouts");
    const { pathnameValues, pathname } = usePathnameWithLocal();

    const scope = useMemo(() => {
        const chunk = pathnameValues[0] as MenuScopeType;
        switch (chunk) {
            case "casino":
            case "sport":
                return chunk as MenuScopeType;
            default:
                return "default";
        }
    }, [pathnameValues]);

    const renderMainNavigation = useCallback(
        (name: string) => {
            switch (name) {
                case MenuButtonsEnum.Prematch:
                    return (
                        <ActiveLink
                            key={name}
                            href={`/${RouteNames.SPORT}/${RouteNames.PREMATCH}/${RouteNames.TOP}`}
                            includes={`/${RouteNames.SPORT}/${RouteNames.PREMATCH}`}
                            activeClassName={styles.active}
                            className={classNames(styles.navItem, styles.sportNavItem)}>
                            <>
                                <Svg
                                    src={`/${AssetsDirectory.Mobile}/menu-sport.svg`}
                                    className="svg-icon"
                                    prefixAppName
                                />
                                <span>
                                    <Translate tid="pageName_sport" />
                                </span>
                            </>
                        </ActiveLink>
                    );
                case MenuButtonsEnum.LiveCasino: {
                    return (
                        <ActiveLink
                            key={name}
                            href={`/${RouteNames.CASINO}/${RouteNames.LIVE_CASINO}`}
                            includes={`/${RouteNames.CASINO}/${RouteNames.LIVE_CASINO}`}
                            activeClassName={styles.active}
                            className={classNames(styles.navItem, styles.liveNavItem)}>
                            <>
                                <Svg
                                    src={`/${AssetsDirectory.Common}/${AssetsDirectory.BaseIcons}/live-casino-icon.svg`}
                                    className="svg-icon"
                                    prefixAppName
                                />
                                <span>
                                    <Translate tid="pageName_livecasino" />
                                </span>
                            </>
                        </ActiveLink>
                    );
                }
                case MenuButtonsEnum.Slots: {
                    return (
                        <ActiveLink
                            key={name}
                            href={`/${RouteNames.CASINO}/${RouteNames.SLOTS}`}
                            includes={`/${RouteNames.CASINO}/${RouteNames.SLOTS}`}
                            activeClassName={styles.active}
                            className={classNames(styles.navItem, styles.casinoNavItem)}>
                            <>
                                <Svg
                                    src={`/${AssetsDirectory.Common}/${AssetsDirectory.BaseIcons}/slots-icon.svg`}
                                    className="svg-icon"
                                    prefixAppName
                                />
                                <span>
                                    <Translate tid="pageName_slots" />
                                </span>
                            </>
                        </ActiveLink>
                    );
                }
                case MenuButtonsEnum.Live:
                    return (
                        <ActiveLink
                            key={name}
                            href={`/${RouteNames.SPORT}/${RouteNames.LIVE}/${RouteNames.ALL}`}
                            includes={`/${RouteNames.SPORT}/${RouteNames.LIVE}`}
                            forceActive={
                                pathname.includes(`/${RouteNames.SPORT}/${RouteNames.CALENDAR}`) ||
                                pathname.includes(`/${RouteNames.SPORT}/${RouteNames.UPCOMING}`)
                            }
                            activeClassName={styles.active}
                            className={classNames(styles.navItem, styles.liveNavItem)}>
                            <>
                                <Svg
                                    src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/menu-live.svg`}
                                    className="svg-icon"
                                />
                                <span>
                                    <Translate tid="pageName_live" />
                                </span>
                            </>
                        </ActiveLink>
                    );
                case MenuButtonsEnum.Casino:
                    return (
                        <ActiveLink
                            key={name}
                            href={`/${RouteNames.CASINO}`}
                            activeClassName={styles.active}
                            className={classNames(styles.navItem, styles.casinoNavItem)}>
                            <>
                                <Svg
                                    src={`/${AssetsDirectory.Mobile}/menu-casino.svg`}
                                    className="svg-icon"
                                    prefixAppName
                                />
                                <span>
                                    <Translate tid="pageName_casino" />
                                </span>
                            </>
                        </ActiveLink>
                    );
                default:
                    return null;
            }
        },
        [pathname],
    );

    return (
        <nav className={styles.nav}>
            {siteLayoutsConfig?.menu?.[scope]?.map(renderMainNavigation)}
        </nav>
    );
};

export default SidebarMainNavigation;
